import React, { useEffect, useState } from 'react';
import ReactPaginate from 'react-paginate';
import useDebounce from '../../hooks/useDebounce';
import { useResponsive } from '../../hooks/useResponsive';
import { TableQueryState } from '../../hooks/useTableQuery';

export interface DataTableColumn<T> {
  key: string;
  label: string;
  sortable?: boolean;
  searchable?: boolean;
  // isMobile lets a column render differently on the card view (e.g. icon buttons vs full buttons)
  render?: (row: T, index: number, isMobile: boolean) => React.ReactNode;
  // Where this column lands in the mobile card layout. Defaults to 'field'.
  cardRole?: 'title' | 'subtitle' | 'actions' | 'field';
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  count: number;
  rowKey: (row: T, index: number) => string | number;
  query: TableQueryState;
  loading?: boolean;
  emptyMessage?: string;
  // Opt-in row selection: renders a checkbox column plus a select-all-on-page
  // header checkbox. The parent owns the selected keys.
  selectable?: boolean;
  selectedKeys?: (string | number)[];
  onSelectionChange?: (keys: (string | number)[]) => void;
}

const FilterInput: React.FC<{ value: string; onChange: (value: string) => void; placeholder: string }> = ({ value, onChange, placeholder }) => {
  const [local, setLocal] = useState(value);
  const debounced = useDebounce(local, 400);

  useEffect(() => {
    setLocal(value);
  }, [value]);

  useEffect(() => {
    if (debounced !== value) {
      onChange(debounced);
    }
  }, [debounced]);

  return (
    <input
      className="dtable-filter-input"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      placeholder={placeholder}
      aria-label={`جستجو در ${placeholder}`}
    />
  );
};

const DataTable = <T,>({ columns, rows, count, rowKey, query, loading, emptyMessage, selectable, selectedKeys = [], onSelectionChange }: DataTableProps<T>) => {
  const { isMobile } = useResponsive();
  const hasSearchableColumn = columns.some((c) => c.searchable);
  const pageCount = Math.max(1, Math.ceil((count || 0) / query.perPage));
  const columnCount = columns.length + (selectable ? 1 : 0);

  const cellValue = (col: DataTableColumn<T>, row: T, index: number) =>
    col.render ? col.render(row, index, isMobile) : (row as any)[col.key];

  const pageKeys = rows.map((row, index) => rowKey(row, index));
  const isSelected = (key: string | number) => selectedKeys.includes(key);
  const allPageSelected = pageKeys.length > 0 && pageKeys.every(isSelected);
  const somePageSelected = pageKeys.some(isSelected);

  const toggleRow = (key: string | number) => {
    if (!onSelectionChange) return;
    onSelectionChange(
      isSelected(key) ? selectedKeys.filter((k) => k !== key) : [...selectedKeys, key],
    );
  };

  const toggleAllOnPage = () => {
    if (!onSelectionChange) return;
    onSelectionChange(
      allPageSelected
        ? selectedKeys.filter((k) => !pageKeys.includes(k))
        : [...selectedKeys, ...pageKeys.filter((k) => !isSelected(k))],
    );
  };

  const rowCheckbox = (key: string | number) => (
    <input
      type="checkbox"
      className="dtable-checkbox"
      checked={isSelected(key)}
      onChange={() => toggleRow(key)}
      aria-label="انتخاب ردیف"
    />
  );

  const renderSortIndicator = (key: string) => {
    if (query.sortBy !== key) return null;
    return <span className="dtable-sort-arrow">{query.sortDir === 'ASC' ? ' ▲' : ' ▼'}</span>;
  };

  const pagination = pageCount > 1 && (
    <ReactPaginate
      breakLabel="..."
      nextLabel="بعدی >"
      onPageChange={(event) => query.setPage(event.selected + 1)}
      forcePage={query.page - 1}
      pageRangeDisplayed={isMobile ? 3 : 5}
      pageCount={pageCount}
      previousLabel="< قبلی"
      renderOnZeroPageCount={null}
      className="pagination"
      pageClassName="paginationBreak"
      previousClassName="paginationBreak"
      nextClassName="paginationBreak"
      activeClassName="paginationActive"
    />
  );

  const emptyText = emptyMessage || 'موردی یافت نشد';

  if (isMobile) {
    const titleCol = columns.find((c) => c.cardRole === 'title');
    const subtitleCol = columns.find((c) => c.cardRole === 'subtitle');
    const actionsCol = columns.find((c) => c.cardRole === 'actions');
    const fieldCols = columns.filter((c) => !c.cardRole || c.cardRole === 'field');

    return (
      <>
        {pagination}
        <div className="users-mobile-container dtable-mobile-container">
          {loading ? null : rows.length > 0 ? (
            rows.map((row, index) => (
              <div className="user-card-mobile dtable-card" key={rowKey(row, index)}>
                <div className="user-card-header">
                  <div className="user-info">
                    {selectable && rowCheckbox(rowKey(row, index))}
                    {titleCol && <h3 className="user-name">{cellValue(titleCol, row, index)}</h3>}
                    {subtitleCol && <p className="user-phone">{cellValue(subtitleCol, row, index)}</p>}
                  </div>
                  {actionsCol && <div className="user-actions">{cellValue(actionsCol, row, index)}</div>}
                </div>
                <div className="user-card-body">
                  {fieldCols.map((col) => (
                    <div className="user-detail" key={col.key}>
                      <span className="detail-label">{col.label}:</span>
                      <span className="detail-value">{cellValue(col, row, index)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="no-users-message">
              <p>{emptyText}</p>
            </div>
          )}
        </div>
        {pagination}
      </>
    );
  }

  return (
    <>
      {pagination}
      <div className="table-container">
        <table>
          <thead>
            <tr className="dashTr1">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={col.sortable ? 'clickable dtable-sortable-th' : ''}
                  onClick={col.sortable ? () => query.setSort(col.key) : undefined}
                  role={col.sortable ? 'button' : undefined}
                  tabIndex={col.sortable ? 0 : undefined}
                  onKeyDown={col.sortable ? (e) => e.key === 'Enter' && query.setSort(col.key) : undefined}
                >
                  {col.label}
                  {col.sortable && renderSortIndicator(col.key)}
                </th>
              ))}
              {/* The table renders LTR, so this trailing cell is the leading
                  (right-hand) edge for the RTL reader. */}
              {selectable && (
                <th className="dtable-checkbox-cell">
                  <input
                    type="checkbox"
                    className="dtable-checkbox"
                    checked={allPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = somePageSelected && !allPageSelected;
                    }}
                    onChange={toggleAllOnPage}
                    aria-label="انتخاب همه در این صفحه"
                  />
                </th>
              )}
            </tr>
            {hasSearchableColumn && (
              <tr className="dtable-filter-row">
                {columns.map((col) => (
                  <th key={col.key}>
                    {col.searchable && (
                      <FilterInput
                        value={query.filters[col.key] || ''}
                        onChange={(value) => query.setFilter(col.key, value)}
                        placeholder={col.label}
                      />
                    )}
                  </th>
                ))}
                {selectable && <th />}
              </tr>
            )}
          </thead>
          <tbody>
            {!loading && rows.length > 0 ? (
              rows.map((row, index) => (
                <tr className="dashTr2" key={rowKey(row, index)}>
                  {columns.map((col) => (
                    <td key={col.key}>{cellValue(col, row, index)}</td>
                  ))}
                  {selectable && (
                    <td className="dtable-checkbox-cell">{rowCheckbox(rowKey(row, index))}</td>
                  )}
                </tr>
              ))
            ) : !loading ? (
              <tr>
                <td colSpan={columnCount} className="no-users-row">
                  {emptyText}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {pagination}
    </>
  );
};

export default DataTable;
